import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import Checkbox from '@mui/material/Checkbox';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import { useQueries } from '@tanstack/react-query';
import type { FC } from 'react';
import { Fragment, useEffect, useMemo, useRef } from 'react';
import type { UseFormReturn } from 'react-hook-form';
import { FormProvider } from 'react-hook-form';

import { getAdvisorNumberListApi, getTechNumberListApi } from 'api/tickets';
import DotsLoader from 'components/DotsLoader/DotsLoader';
import FlexContainer from 'components/FlexContainer/FlexContainer';
import MuiFormAutocomplete from 'components/Form/MuiFormAutocomplete/MuiFormAutocomplete';
import MuiFormInput from 'components/Form/MuiFormInput/MuiFormInput';
import MuiFormSelect from 'components/Form/MuiFormSelect/MuiFormSelect';
import { useTickets } from 'contexts/TicketsContext';
import type { GetTicketsResponseDTO } from 'declarations/dto/tickets';
import { RequestQueryKeysE } from 'declarations/enums/react-query';
import { ActiveTabTypeE } from 'declarations/enums/tab';
import { TicketAssigneeE } from 'declarations/enums/tickets';
import type { NoopT } from 'declarations/types/common';
import type { SelectOptionT } from 'declarations/types/select';
import { formatAmount } from 'helpers/locale';
import { convertNumbersToSelectOptions } from 'helpers/selectOptions';
import { getLastMessageDisplayText, mapAgeToColor } from 'pages/TicketsPage/helpers';
import type { ChangeTicketsListFormValues } from 'pages/TicketsPage/hooks/useTicketsPage';

import useChangeTicketDetailsForm from '../TicketDetailsPopup/hooks/useChangeTicketDetailsForm';

import TicketsCardViewLoader from './components/TicketsCardViewLoader';
import {
  CardCellStyled,
  CardCheckboxWrapStyled,
  CardContentStyled,
  CardInputWrapStyled,
  CardStyled,
  CardViewStyled,
  FormStyled,
  TextLabel,
  TextRoNumberStyled,
  TextValueStyled,
} from './TicketsCardView.styled';

/** Claim status IDs that require OEM assignee. 1=Pending, 7=ManualReview. */
const OEM_RESERVED_CLAIM_STATUS_IDS = [1, 7];

export type TicketsCardViewPropsT = {
  ticketsPages: GetTicketsResponseDTO[] | undefined | null;
  isLoadingTickets: boolean;
  onSelectTicket: (id: string, payload: { roNumber: string; lineNumber: string | null }) => NoopT;
  onSaveChanges: (data: Record<string, ChangeTicketsListFormValues>) => Promise<void>;
  formMethods: UseFormReturn<Record<string, ChangeTicketsListFormValues>>;
};

const compactInputSx = {
  '& .MuiOutlinedInput-root': { minHeight: 32, height: 32 },
  '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '6px 10px' },
  '& .MuiSelect-select': { padding: '6px 10px', fontSize: '0.75rem' },
};

const CARD_LIST_NEAR_BOTTOM_PX = 200;
const CARD_LIST_MIN_SCROLL_TOP_FOR_APPEND = 40;

const TicketsCardView: FC<TicketsCardViewPropsT> = ({
  ticketsPages,
  isLoadingTickets,
  onSelectTicket,
  onSaveChanges,
  formMethods,
}) => {
  const {
    ticketListScrollContainerRef,
    isFetchingNextTicketsPage,
    isFetchingTickets,
    hasNextTicketsPage,
    fetchNextTicketsPage,
    selectedTicketsIds,
    setSelectedTicketsIds,
    setSelectedTicketsRooftopIds,
    selectedTicketsRooftopIds,
    isEditMode,
    roofTopsList,
    activeTab,
  } = useTickets();

  const isScheduleSection = activeTab === ActiveTabTypeE.Schedule;

  const {
    claimStatusesOptions,
    ticketSubcategoriesOptions,
    isLoadingTicketSubcategoriesOptions,
    isLoadingClaimStatusesOptions,
  } = useChangeTicketDetailsForm();

  const editModeRoofTopIds = useMemo((): string[] => {
    if (!isEditMode || selectedTicketsIds.length === 0 || !ticketsPages) return [];
    const allTickets = ticketsPages.flatMap(page => page.data ?? []);
    const ids = new Set<string>();
    selectedTicketsIds.forEach(ticketId => {
      const ticket = allTickets.find(t => t.id === ticketId);
      if (ticket?.roofTop?.id) ids.add(ticket.roofTop.id);
    });
    return Array.from(ids);
  }, [isEditMode, selectedTicketsIds, ticketsPages]);

  const advisorQueries = useQueries({
    queries: editModeRoofTopIds.map(roofTopId => ({
      queryKey: [RequestQueryKeysE.FetchAdvisorNumberList, roofTopId],
      queryFn: (): Promise<Parameters<typeof convertNumbersToSelectOptions>[0]> =>
        getAdvisorNumberListApi(roofTopId),
      enabled: editModeRoofTopIds.length > 0,
    })),
  });

  const techQueries = useQueries({
    queries: editModeRoofTopIds.map(roofTopId => ({
      queryKey: [RequestQueryKeysE.FetchTechNumberList, roofTopId],
      queryFn: (): Promise<Parameters<typeof convertNumbersToSelectOptions>[0]> =>
        getTechNumberListApi(roofTopId),
      enabled: editModeRoofTopIds.length > 0,
    })),
  });

  const advisorOptionsByRoofTop = useMemo((): Record<string, SelectOptionT[]> => {
    const map: Record<string, SelectOptionT[]> = {};
    editModeRoofTopIds.forEach((roofTopId, i) => {
      const result = advisorQueries[i]?.data;
      map[roofTopId] = result ? convertNumbersToSelectOptions(result) : [];
    });
    return map;
  }, [editModeRoofTopIds, advisorQueries]);

  const techOptionsByRoofTop = useMemo((): Record<string, SelectOptionT[]> => {
    const map: Record<string, SelectOptionT[]> = {};
    editModeRoofTopIds.forEach((roofTopId, i) => {
      const result = techQueries[i]?.data;
      map[roofTopId] = result ? convertNumbersToSelectOptions(result) : [];
    });
    return map;
  }, [editModeRoofTopIds, techQueries]);

  const getAssigneeOptions = (roofTopId: string): SelectOptionT[] => {
    const targetRooftop = roofTopsList?.find(mappedRooftop => mappedRooftop.id === roofTopId);
    const dealerLabel = targetRooftop?.name ?? 'Chirp';
    const dealerValue = targetRooftop?.name ?? TicketAssigneeE.Chirp;
    return [
      { label: dealerLabel, value: dealerValue },
      { label: TicketAssigneeE.WarrCloud, value: TicketAssigneeE.WarrCloud },
      {
        label: 'OEM',
        value: 'OEM',
        disabled: true,
        disabledTooltip:
          'Selecting the correct claim status will automatically assign this ticket to the OEM.',
      },
    ];
  };

  const getAdvisorOptions = (roofTopId: string): SelectOptionT[] =>
    advisorOptionsByRoofTop[roofTopId] ?? [];
  const getTechOptions = (roofTopId: string): SelectOptionT[] =>
    techOptionsByRoofTop[roofTopId] ?? [];

  const handleCheckClick =
    (ticketId: string, roofTopId: string) =>
    (e: React.MouseEvent): void => {
      e.stopPropagation();
      if (selectedTicketsIds.includes(ticketId)) {
        setSelectedTicketsIds(selectedTicketsIds.filter(id => id !== ticketId));
      } else {
        setSelectedTicketsIds([...selectedTicketsIds, ticketId]);
        if (roofTopId && !selectedTicketsRooftopIds.includes(roofTopId)) {
          setSelectedTicketsRooftopIds([...selectedTicketsRooftopIds, roofTopId]);
        }
      }
    };

  const ticketsAppendLockRef = useRef(false);

  useEffect(() => {
    if (!isFetchingNextTicketsPage) {
      ticketsAppendLockRef.current = false;
    }
  }, [isFetchingNextTicketsPage]);

  const ticketsPageCount = ticketsPages?.length ?? 0;
  const ticketCardRowCount = ticketsPages?.reduce((n, p) => n + (p.data?.length ?? 0), 0) ?? 0;

  useEffect(() => {
    const el = ticketListScrollContainerRef.current;
    if (!el) return;

    const tryAppendNextPage = (): void => {
      if (!hasNextTicketsPage || isFetchingNextTicketsPage || ticketsAppendLockRef.current) {
        return;
      }
      const { scrollTop, clientHeight, scrollHeight } = el;
      if (scrollTop < CARD_LIST_MIN_SCROLL_TOP_FOR_APPEND) {
        return;
      }
      if (scrollTop + clientHeight < scrollHeight - CARD_LIST_NEAR_BOTTOM_PX) {
        return;
      }
      ticketsAppendLockRef.current = true;
      fetchNextTicketsPage();
    };

    const onScroll = (): void => {
      tryAppendNextPage();
    };

    el.addEventListener('scroll', onScroll, { passive: true });
    tryAppendNextPage();

    return (): void => {
      el.removeEventListener('scroll', onScroll);
    };
  }, [
    ticketCardRowCount,
    ticketsPageCount,
    hasNextTicketsPage,
    isFetchingNextTicketsPage,
    fetchNextTicketsPage,
    ticketListScrollContainerRef,
    isLoadingTickets,
  ]);

  const shouldLoadMore = isFetchingTickets && isFetchingNextTicketsPage && hasNextTicketsPage;
  const { handleSubmit } = formMethods;
  const watchedFormValues = formMethods.watch();

  useEffect(() => {
    selectedTicketsIds.forEach(ticketId => {
      const ticketFormValues =
        watchedFormValues?.[`[${ticketId}]`] ?? watchedFormValues?.[ticketId];
      const ticket = ticketsPages?.flatMap(page => page.data).find(t => t?.id === ticketId);
      const claimStatusValue = Number(
        ticketFormValues?.claimStatusId ?? ticket?.claimStatusId ?? 0
      );
      const setValue = formMethods.setValue as (
        name: string,
        value: unknown,
        options?: { shouldDirty?: boolean; shouldTouch?: boolean }
      ) => void;
      if (OEM_RESERVED_CLAIM_STATUS_IDS.includes(claimStatusValue)) {
        setValue(`[${ticketId}].assigneeName`, 'OEM', {
          shouldDirty: true,
          shouldTouch: true,
        });
      } else {
        const currentAssignee = ticketFormValues?.assigneeName ?? ticket?.assigneeName ?? '';
        if (currentAssignee.trim().toUpperCase() === 'OEM') {
          setValue(`[${ticketId}].assigneeName`, TicketAssigneeE.WarrCloud, {
            shouldDirty: true,
            shouldTouch: true,
          });
        }
      }
    });
  }, [selectedTicketsIds, ticketsPages, watchedFormValues, formMethods]);

  const normalizeString = (str: string): string => str?.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();

  useEffect(() => {
    const findOptionIdByName = (
      options: { label: string; value: string | number }[],
      name: string | null
    ): string | number | null =>
      options.find(
        (option): boolean => normalizeString(option.label) === normalizeString(name || '')
      )?.value || null;

    formMethods.reset();
    selectedTicketsIds.forEach(ticketId => {
      const ticket = ticketsPages?.flatMap(page => page.data).find(t => t.id === ticketId);

      if (ticket) {
        const subCategoryName = ticket.subCategory ?? null;
        const subCategoryId: number | null =
          (ticket.subCategoryId as number | null) ??
          (findOptionIdByName(ticketSubcategoriesOptions, subCategoryName) as number | null);
        const priorityId = ticket.ticketPriorityId ? ticket.ticketPriorityId : 1;

        const parsedLastMsg = getLastMessageDisplayText(ticket.lastMessage);

        const setValue = formMethods.setValue as (name: string, value: unknown) => void;
        setValue(`[${ticketId}].claimStatusId`, ticket.claimStatusId ?? 1);
        setValue(`[${ticketId}].ticketPriorityId`, priorityId);
        setValue(`[${ticketId}].subCategoryId`, subCategoryId || null);
        const rooftopForAssignee = roofTopsList?.find(r => r.id === ticket.roofTop.id);
        const assigneeDisplayValue =
          ticket.assigneeName === TicketAssigneeE.Chirp && rooftopForAssignee
            ? rooftopForAssignee.name
            : ticket.assigneeName ?? '';
        setValue(`[${ticketId}].assigneeName`, assigneeDisplayValue);
        setValue(`[${ticketId}].amount`, String(ticket.amount ?? 0));
        setValue(
          `[${ticketId}].dmsScheduleAccountNumber`,
          ticket.dmsScheduleAccountNumber != null ? String(ticket.dmsScheduleAccountNumber) : null
        );
        setValue(`[${ticketId}].advisorNumber`, ticket.advisorNumber ?? '');
        setValue(`[${ticketId}].techNumber`, ticket.techNumber ?? '');
        setValue(`[${ticketId}].text`, parsedLastMsg);
      }
    });
  }, [selectedTicketsIds, ticketsPages, formMethods, ticketSubcategoriesOptions, roofTopsList]);

  const renderCellContent = (
    ticketId: string,
    roofTopId: string,
    field: string,
    value: React.ReactNode,
    isEditable: boolean,
    currentValues?: { techNumber?: string; advisorNumber?: string }
  ): React.ReactNode => {
    if (!isEditable || !isEditMode || !selectedTicketsIds.includes(ticketId)) {
      return value;
    }

    const name = `[${ticketId}].${field}` as never;

    if (field === 'assigneeName') {
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          <MuiFormSelect
            label="Assignee"
            name={name}
            placeholder=""
            options={getAssigneeOptions(roofTopId)}
            sx={compactInputSx}
          />
        </CardInputWrapStyled>
      );
    }
    if (field === 'subCategoryId') {
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          <MuiFormSelect
            label="Subcategory"
            name={name}
            placeholder="Subcategories"
            options={ticketSubcategoriesOptions}
            isLoading={isLoadingTicketSubcategoriesOptions}
            searchable
            sx={compactInputSx}
          />
        </CardInputWrapStyled>
      );
    }
    if (field === 'claimStatusId') {
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          <MuiFormSelect
            label="Claim Status"
            name={name}
            placeholder="Claim statuses"
            options={claimStatusesOptions}
            isLoading={isLoadingClaimStatusesOptions}
            sx={compactInputSx}
          />
        </CardInputWrapStyled>
      );
    }
    if (field === 'amount') {
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          <MuiFormInput
            label="Amount"
            name={name}
            placeholder="0.00"
            type="number"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <AttachMoneyIcon fontSize="small" sx={{ fontSize: 16 }} />
                </InputAdornment>
              ),
            }}
          />
        </CardInputWrapStyled>
      );
    }
    if (field === 'advisorNumber') {
      const advisorOptions = getAdvisorOptions(roofTopId);
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          {advisorOptions.length > 0 ? (
            <MuiFormAutocomplete
              label="Advisor"
              name={name}
              placeholder="Select or enter advisor"
              options={advisorOptions}
              allowCustomInput
            />
          ) : (
            <MuiFormInput label="Advisor" name={name} placeholder="Enter advisor" />
          )}
        </CardInputWrapStyled>
      );
    }
    if (field === 'techNumber') {
      const techOptions = getTechOptions(roofTopId);
      const currentTech = currentValues?.techNumber;
      const optionsWithCurrent =
        currentTech &&
        !techOptions.some(o => String(o.value) === currentTech || o.label === currentTech)
          ? [...techOptions, { label: currentTech, value: currentTech }]
          : techOptions;
      return (
        <CardInputWrapStyled onClick={(e): void => e.stopPropagation()}>
          <MuiFormSelect
            label="Tech"
            name={name}
            placeholder="Tech"
            options={optionsWithCurrent}
            isLoading={false}
            sx={compactInputSx}
          />
        </CardInputWrapStyled>
      );
    }

    return value;
  };

  return (
    <FormProvider {...formMethods}>
      <FormStyled role="form" onSubmit={handleSubmit(onSaveChanges)}>
        {isLoadingTickets && !isFetchingNextTicketsPage ? (
          <TicketsCardViewLoader />
        ) : (
          <CardViewStyled data-testid="tickets-cards">
            {ticketsPages?.map(
              (page, index): JSX.Element => (
                <Fragment key={`page-${index}`}>
                  {page.data?.map(
                    ({
                      id,
                      roNumber,
                      age,
                      assigneeName,
                      lineNumber,
                      statusId,
                      categoryName,
                      roofTop,
                      claimAge,
                      claimStatusId: _claimStatusId,
                      subCategory,
                      requestSubCategoryName,
                      claimStatus,
                      amount,
                      advisorNumber,
                      techNumber,
                    }): JSX.Element => {
                      const roofTopId = roofTop?.id ?? '';
                      const isSchedule = categoryName?.toLowerCase() === 'schedule';
                      return (
                        <CardStyled
                          key={id}
                          onClick={onSelectTicket(id, {
                            roNumber,
                            lineNumber: lineNumber ?? null,
                          })}
                        >
                          <CardCheckboxWrapStyled
                            onClick={(e): void => e.stopPropagation()}
                            onMouseDown={(e): void => e.stopPropagation()}
                          >
                            <Checkbox
                              size="medium"
                              color="primary"
                              disableRipple
                              checked={selectedTicketsIds.includes(id)}
                              onClick={handleCheckClick(id, roofTopId)}
                              sx={{
                                opacity: 1,
                                visibility: 'visible',
                                display: 'flex',
                                flexShrink: 0,
                                width: 42,
                                height: 42,
                                minWidth: 42,
                                minHeight: 42,
                                padding: 0,
                                '& .MuiSvgIcon-root': { fontSize: 24 },
                              }}
                            />
                          </CardCheckboxWrapStyled>
                          <CardContentStyled>
                            <CardCellStyled $col={1} $row={1}>
                              <TextLabel>RO</TextLabel>
                              <TextRoNumberStyled variant="body3" color="primaryText">
                                {roNumber}
                              </TextRoNumberStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={2} $row={1}>
                              <TextLabel>Status</TextLabel>
                              <TextValueStyled variant="body3" color="primaryText">
                                {statusId ?? '-'}
                              </TextValueStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={3} $row={1}>
                              <TextLabel>Age</TextLabel>
                              <TextValueStyled variant="body3" color={mapAgeToColor(age)}>
                                {age}
                              </TextValueStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={1} $row={2}>
                              <TextLabel>Line</TextLabel>
                              <TextValueStyled variant="body3" color="primaryText">
                                {lineNumber || '-'}
                              </TextValueStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={2} $row={2}>
                              <TextLabel>Assignee</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'assigneeName',
                                <TextValueStyled variant="body3" color="primaryText">
                                  {assigneeName}
                                </TextValueStyled>,
                                true
                              )}
                            </CardCellStyled>
                            <CardCellStyled $col={3} $row={2}>
                              <TextLabel>Category</TextLabel>
                              <TextValueStyled variant="body3" color="primaryText">
                                {categoryName || '-'}
                              </TextValueStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={1} $row={3}>
                              <TextLabel>Claim Age</TextLabel>
                              <TextValueStyled
                                variant="body3"
                                color={claimAge ? mapAgeToColor(claimAge) : 'primaryText'}
                              >
                                {claimAge ?? '-'}
                              </TextValueStyled>
                            </CardCellStyled>
                            <CardCellStyled $col={2} $row={3}>
                              <TextLabel>Subcategory</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'subCategoryId',
                                <Tooltip
                                  title={subCategory ?? requestSubCategoryName ?? '-'}
                                  enterDelay={400}
                                  arrow
                                  placement="top-start"
                                >
                                  <TextValueStyled variant="body3" color="primaryText">
                                    {subCategory ?? requestSubCategoryName ?? '-'}
                                  </TextValueStyled>
                                </Tooltip>,
                                isSchedule
                              )}
                            </CardCellStyled>
                            <CardCellStyled $col={3} $row={3}>
                              <TextLabel>Claim Status</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'claimStatusId',
                                <TextValueStyled variant="body3" color="primaryText">
                                  {claimStatus || '-'}
                                </TextValueStyled>,
                                true
                              )}
                            </CardCellStyled>
                            <CardCellStyled $col={1} $row={4}>
                              <TextLabel>Amount</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'amount',
                                <TextValueStyled variant="body3" color="primaryText">
                                  {amount != null ? formatAmount(amount) : '-'}
                                </TextValueStyled>,
                                !isScheduleSection
                              )}
                            </CardCellStyled>
                            <CardCellStyled $col={2} $row={4}>
                              <TextLabel>Advisor</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'advisorNumber',
                                <TextValueStyled variant="body3" color="primaryText">
                                  {advisorNumber || '-'}
                                </TextValueStyled>,
                                true
                              )}
                            </CardCellStyled>
                            <CardCellStyled $col={3} $row={4}>
                              <TextLabel>Tech</TextLabel>
                              {renderCellContent(
                                id,
                                roofTopId,
                                'techNumber',
                                <TextValueStyled variant="body3" color="primaryText">
                                  {techNumber || '-'}
                                </TextValueStyled>,
                                true,
                                { techNumber: techNumber ?? undefined }
                              )}
                            </CardCellStyled>
                          </CardContentStyled>
                        </CardStyled>
                      );
                    }
                  )}
                </Fragment>
              )
            )}
          </CardViewStyled>
        )}
        {shouldLoadMore && (
          <FlexContainer justify="around">
            <DotsLoader />
          </FlexContainer>
        )}
      </FormStyled>
    </FormProvider>
  );
};

export default TicketsCardView;
